import deepEqual from 'deep-equal'
import debounce from 'lodash/debounce'
import {
  array,
  bool,
  func,
  number,
  object,
  oneOfType,
  string,
} from 'prop-types'
import React from 'react'
import { PositionedArrayManager } from '@opallabs/lambda'
import { testFeature } from '@opallabs/principles'
import { track } from '@opallabs/quanta'
import { isApproval } from '../helpers/matchers'
import {
  annotatedPhases,
  findCompletedPhases,
  needsPushBasedRefetch,
  shouldFetchWorkflow,
} from './helpers/helpers'
import { enablePhaseLoadingState } from './helpers/stateHelpers'
import UnmanagedPhaseList from './unmanaged'

function trackPhaseUpdate(phase, update, contentId) {
  // tracking for setting a due date on a Phase
  if (update.due_date) {
    track('Workflow: Schedule', {
      phaseType: phase.phase_type,
      contentId,
    })
  }
}

export default class PhaseList extends React.Component {
  static propTypes = {
    attachmentId: oneOfType([string, number]),
    contentAccountId: number,
    createApprovalItem: func,
    createPhase: func,
    createPhaseItem: func,
    createWorkflow: func,
    currentUser: object,
    destroyPhase: func,
    destroyPhaseItem: func,
    entitySaveMethod: func,
    finalApprovalReached: bool,
    getIntegrations: func,
    getWorkflow: func,
    isLimitedRole: bool,
    isLocalizationTemplate: bool,
    isSessionTokenLoaded: bool,
    isStoryLevelTemplate: bool,
    isTemplate: bool,
    isWorkflowLockable: bool,
    localizationTemplatePostId: number,
    locked: bool,
    onNewActivePhase: func,
    onRetractApprovals: func,
    onSendForApproval: func,
    onStartWorkflow: func,
    onUpdatePhase: func,
    onUpdatePhaseItem: func,
    parentPostId: number,
    phases: array,
    postId: number,
    resendApprovals: func,
    resetWSLastFrame: func,
    retractApprovals: func,
    sendForApproval: func,
    startWorkflow: func,
    storyId: number,
    theme: string,
    toggleWorkflowLock: func,
    updatePhase: func,
    updatePhaseItem: func,
    updateWorkflow: func,
    workflow: object,
    workflowLoaded: bool,
    workflowLocation: string,
    workflowStarted: bool,
    workflowType: string,
    workspaceId: number,
    ws_frames: object,
  }

  static defaultProps = {
    workflowLocation: '',
  }

  constructor(props) {
    super(props)

    this.positionManager = new PositionedArrayManager()

    this.state = {
      blockFetch: false,
      completedPhases: findCompletedPhases(props.phases) || [],
      enableListManagerEditing: true,
      locked: props.locked,
      phases: props.phases || [],
    }

    this.debouncedGetWorkflow = debounce(this.getWorkflow.bind(this), 400, {
      leading: true,
      trailing: false,
    })

    if (testFeature('project_management_integration')) {
      if (props.workflowLocation !== 'workflowPopover') {
        props.getIntegrations()
      }
    }
  }

  UNSAFE_componentWillMount() {
    if (this.props.isStoryLevelTemplate || this.props.isLocalizationTemplate) {
      this.props.createWorkflow({
        attachmentId: this.props.attachmentId,
        parentPostId: this.props.parentPostId,
        brandId: this.props.workspaceId,
      })
    } else if (
      shouldFetchWorkflow({
        props: this.props,
        attachmentId: this.props.attachmentId,
        workflowType: this.props.workflowType,
      })
    ) {
      this.debouncedGetWorkflow({
        showLoader: true,
        contentAccountId: this.props.contentAccountId,
      })
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.onNewActivePhase) {
      const previouslyActivePhase = this.state.phases.find(
        (phase) => phase.is_active
      )
      const activePhase = nextProps.phases.find((phase) => phase.is_active)
      const diffPhase =
        !previouslyActivePhase ||
        (previouslyActivePhase && !activePhase) ||
        activePhase.id !== previouslyActivePhase.id

      if (activePhase && diffPhase) {
        this.props.onNewActivePhase()
      }
    }

    if (needsPushBasedRefetch(nextProps) && !this.state.blockFetch) {
      this.debouncedGetWorkflow()
    }

    if (nextProps.locked !== this.props.locked) {
      this.setState({
        locked: nextProps.locked,
      })
    }

    if (nextProps.workflow.phases !== this.props.workflow.phases) {
      this.setState((lastState) => {
        const nextCompletedPhases = findCompletedPhases(nextProps.phases) || []
        // we only need to update 'completedPhases' on state when a Phase has been ADDED to the list
        // otherwise we need to ignore it to avoid accidentally emptying the local list
        const completedPhases =
          nextCompletedPhases.length > lastState.completedPhases.length
            ? nextCompletedPhases
            : lastState.completedPhases
        return {
          completedPhases,
          phases: nextProps.workflow.phases || [],
        }
      })
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const webSocketsUnchanged = deepEqual(
      this.props.ws_frames,
      nextProps.ws_frames
    )
    const blockFetchChangedToFalse =
      this.state.blockFetch !== nextState.blockFetch &&
      nextState.blockFetch === false

    return webSocketsUnchanged && !blockFetchChangedToFalse
  }

  componentDidUpdate(prevProps) {
    // check if workflow has JUST started with this update
    // if so, we will set state ONCE to enable the associated flag,
    // which will set ListManager's 'editing' mode back to true, and thus enable us to continue adding Phases
    if (
      !this.state.enableListManagerEditing &&
      this.props.workflowStarted &&
      prevProps.workflowStarted === false
    ) {
      this.setState({ enableListManagerEditing: true })
    }

    // check if the "current" phase has updated since last check
    // if so, we can go ahead and re-enable ListManager's 'editing' state
    const newCurrentPhaseIdx = (this.props.phases || []).findIndex(
      (p) => p.is_current
    )
    if (
      this.currentPhaseIdx === undefined ||
      this.currentPhaseIdx !== newCurrentPhaseIdx
    ) {
      this.currentPhaseIdx = newCurrentPhaseIdx
      this.setState({ enableListManagerEditing: true })
    }
  }

  getWorkflow(showLoader = false) {
    this.props.getWorkflow({
      contentAccountId: this.props.contentAccountId,
      identifier: this.props.attachmentId,
      showLoader,
      type: this.props.workflowType,
    })
  }

  destroyPhase = (phase) =>
    this.props.destroyPhase({
      phaseId: phase.id,
      postId: this.props.postId,
      workflowId: this.props.workflow.id,
      ...this.workflowIdentifiersFromProps(),
    })

  /*
    this check removes identifier and type from wormhole action callback
    arguments in cases where they would break template-specific CRUD calls
  */
  workflowIdentifiersFromProps = () => {
    const identifiers =
      this.props.isStoryLevelTemplate || this.props.isLocalizationTemplate
        ? { identifier: null, type: null }
        : { identifier: this.props.attachmentId, type: this.props.workflowType }
    return identifiers
  }

  createPhase = (phase, update) => {
    track(`Workflow: ${update.phase_type} phase`, {
      contentId: this.props.attachmentId,
    })

    return this.props.createPhase({
      newPhase: {
        ...phase,
        ...update,
      },
      ...this.workflowIdentifiersFromProps(),
      workflowId: this.props.workflow.id,
    })
  }

  updatePhase = (phase, update) => {
    const updateNeedsLoader = update.is_active || update.status

    if (update && updateNeedsLoader) {
      this.setState((prevState) => enablePhaseLoadingState(phase, prevState))

      if (update.is_active === false) {
        return this.retractApprovals(phase)
      }

      if (update.status === 'active') {
        return this.sendForApproval(phase, update)
      }
    }

    trackPhaseUpdate(phase, update, this.props.attachmentId)
    return this.props.updatePhase({
      phaseId: phase.id,
      update,
      workflowId: this.props.workflow.id,
      ...this.workflowIdentifiersFromProps(),
    })
  }

  createPhaseItem = (phase, item, update) => {
    const isApprovalUpdate = update.type && isApproval(update.type)
    const commonProps = {
      identifier: this.props.attachmentId,
      phaseId: phase.id,
      type: this.props.workflowType,
      workflowId: this.props.workflow.id,
    }

    if (isApprovalUpdate) {
      return this.props.createApprovalItem({
        assignee: update.assignee,
        postId: this.props.postId,
        uuid: item.uuid,
        ...commonProps,
        ...this.workflowIdentifiersFromProps(),
      })
    }

    return this.props.createPhaseItem({
      itemId: item.id,
      update: {
        ...update,
        uuid: item.uuid,
      },
      ...commonProps,
      ...this.workflowIdentifiersFromProps(),
    })
  }

  updatePhaseItem = (phase, item, update) => {
    const updater = this.props.updatePhaseItem.bind(this, {
      itemId: item.id,
      phaseId: phase.id,
      update,
      workflowId: this.props.workflow.id,
      ...this.workflowIdentifiersFromProps(),
    })

    // if we have completed this phase with this update, we need to disable ListManager's
    // 'editing' state, so it will clear out any existing placeholder Phases
    if (update.isLastIncompleteItem) {
      return this.setState((prevState) => {
        const { phases } = enablePhaseLoadingState(phase, prevState)
        return {
          enableListManagerEditing: false,
          phases: phases.filter((p) => !p.autoGenerated),
        }
      }, updater)
    }

    return updater()
  }

  destroyPhaseItem = (phase, item) => {
    if (!item.id) {
      return null
    }

    return this.props.destroyPhaseItem({
      itemId: item.id,
      phaseId: phase.id,
      postId: this.props.postId,
      workflowId: this.props.workflow.id,
      ...this.workflowIdentifiersFromProps(),
    })
  }

  get currentPhase() {
    return (
      this.state.phases.find((phase) => phase.is_current) ||
      this.state.phases[0]
    )
  }

  sendForApproval = (phase, update) => {
    const action =
      phase.status === 'denied'
        ? this.props.resendApprovals
        : this.props.sendForApproval

    return action({
      identifier: this.props.attachmentId,
      onSendForApproval: this.props.onSendForApproval,
      phaseId: phase.id,
      resendToDenied: update.resendToDenied,
      type: this.props.workflowType,
      workflowId: this.props.workflow.id,
    })
  }

  retractApprovals = (phase) => {
    if (this.props.retractApprovals) {
      track('Approvals: Retract', {
        contentId: this.props.attachmentId,
        phaseId: phase.id,
      })

      this.setState((prevState) => enablePhaseLoadingState(phase, prevState))

      return this.props.retractApprovals({
        identifier: this.props.attachmentId,
        onRetractApprovals: this.props.onRetractApprovals,
        phaseId: phase.id,
        type: this.props.workflowType,
        workflowId: this.props.workflow.id,
      })
    }
    return null
  }

  toggleWorkflowLock = (locked) => {
    this.setState({ locked })

    return this.props.toggleWorkflowLock({
      identifier: this.props.attachmentId,
      type: this.props.workflowType,
      updatedWorkflow: {
        lock_inherited_phases: locked,
      },
      workflowId: this.props.workflow.id,
    })
  }

  // when starting Workflow for the first time, we want to temporarily disable 'editing' mode
  // in ListManager, in order to remove any existing placeholder Phases
  startWorkflow = () => {
    this.setState({ enableListManagerEditing: false }, () =>
      this.props.startWorkflow({
        identifier: this.props.attachmentId,
        onStartWorkflow: this.props.onStartWorkflow,
        type: this.props.workflowType,
        workflowId: this.props.workflow.id,
      })
    )
  }

  finalApprovalReached = () => {
    const approvalPhases = this.state.phases.filter((currentPhase) =>
      isApproval(currentPhase.phase_type)
    )

    return !!(
      approvalPhases.length &&
      approvalPhases[approvalPhases.length - 1].is_complete
    )
  }

  onPhaseListChanged = (items) => this.setState({ phases: items })

  render() {
    const { locked, phases, enableListManagerEditing } = this.state
    const {
      contentAccountId,
      currentUser,
      entitySaveMethod,
      isTemplate,
      isWorkflowLockable,
      workflowLoaded,
      workflowLocation,
      ...otherProps
    } = this.props

    const modifiedPhases = annotatedPhases(isTemplate, phases)

    return (
      <UnmanagedPhaseList
        {...otherProps}
        attachmentId={this.props.attachmentId}
        completedPhases={findCompletedPhases(phases)}
        contentAccountId={contentAccountId}
        createPhase={this.createPhase}
        createPhaseItem={this.createPhaseItem}
        currentPhase={this.currentPhase}
        currentUser={currentUser}
        enableListManagerEditing={enableListManagerEditing}
        entitySaveMethod={entitySaveMethod}
        getWorkflow={() => this.getWorkflow()}
        isTemplate={isTemplate}
        isWorkflowLockable={isWorkflowLockable}
        loaded={workflowLoaded}
        locked={locked}
        onDestroyPhase={this.destroyPhase}
        onDestroyPhaseItem={this.destroyPhaseItem}
        onItemsChanged={this.onPhaseListChanged}
        onStartWorkflow={this.startWorkflow}
        onToggleWorkflowLock={this.toggleWorkflowLock}
        phases={modifiedPhases}
        retractApprovals={this.retractApprovals}
        sendForApproval={this.sendForApproval}
        theme={this.props.theme}
        updatePhase={this.updatePhase}
        updatePhaseItem={this.updatePhaseItem}
        workflowLocation={workflowLocation}
        workflowType={this.props.workflowType}
      />
    )
  }
}
